<template>
  <div class="empty-results-container">
    <div class="empty-results-content">
      <!-- Improved Empty State Content -->
      <div class="empty-state">
        <div class="empty-state-icon">
          <Icon 
            :icon="icon" 
            class="text-gray-500"
            aria-hidden="true" 
          />
        </div>
        
        <h3 class="empty-state-title">{{ title }}</h3>
        
        <p class="empty-state-message">{{ message }}</p>
        
        <!-- If filters are applied, show what's been filtered -->
        <div v-if="hasFilters" class="applied-filters">
          <p class="applied-filters-title">You're currently filtering by:</p>
          
          <ul class="filter-list">
            <li v-if="filterSearch">Search: "{{ filterSearch }}"</li>
            
            <li v-if="filterPlatforms.length">
              Platforms: {{ filterPlatforms.join(', ') }}
            </li>
            
            <li v-if="filterPricing.length">
              Pricing: {{ filterPricing.join(', ') }}
            </li>
            
            <li v-if="filterTags.length">
              Tags: {{ filterTags.join(', ') }}
            </li>
            
            <li v-if="filterFeatures.length">
              Features: {{ filterFeatures.join(', ') }}
            </li>
          </ul>
        </div>
        
        <!-- Action Button -->
        <button
          @click="clearFilters"
          class="action-button"
          v-if="hasFilters"
        >
          <Icon 
            icon="heroicons:arrow-path" 
            class="w-4 h-4 mr-2" 
            aria-hidden="true"
          />
          Clear All Filters
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Icon } from '@iconify/vue';
import { computed } from 'vue';
import { useForm } from '@inertiajs/vue3';

const props = defineProps({
  title: {
    type: String,
    default: 'No Results Found'
  },
  message: {
    type: String,
    default: 'We couldn\'t find any tools matching your current filters.'
  },
  icon: {
    type: String,
    default: 'heroicons:magnifying-glass'
  },
  filters: {
    type: Object,
    default: () => ({})
  },
  useCase: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['clearFilters']);

// Extract filter values
const filterSearch = computed(() => props.filters.search || '');
const filterPlatforms = computed(() => props.filters.platform || []);
const filterPricing = computed(() => props.filters.pricing_model || []);
const filterTags = computed(() => props.filters.tag || []);
const filterFeatures = computed(() => props.filters.feature || []);

// Check if any filters are applied
const hasFilters = computed(() => {
  return (
    filterSearch.value || 
    filterPlatforms.value.length > 0 || 
    filterPricing.value.length > 0 ||
    filterTags.value.length > 0 ||
    filterFeatures.value.length > 0
  );
});

// Clear all filters and reload
const clearFilters = () => {
  const form = useForm({});
  const path = props.useCase 
    ? `/tools/${props.useCase.slug}`
    : '/tools';
    
  form.get(path);
};
</script>

<style scoped>
.empty-results-container {
  width: 100%;
  padding: 3rem 0;
  display: flex;
  justify-content: center;
}

.empty-results-content {
  max-width: 600px;
  width: 100%;
  margin: 0 1rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem;
  background-color: rgba(31, 41, 55, 0.5);
  border-radius: 0.75rem;
  backdrop-filter: blur(4px);
}

.empty-state-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: rgba(55, 65, 81, 0.5);
  border-radius: 9999px;
  width: 5rem;
  height: 5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
}

.empty-state-message {
  color: #9ca3af; /* gray-400 */
  margin-bottom: 1.5rem;
}

.applied-filters {
  width: 100%;
  max-width: 400px;
  background-color: rgba(55, 65, 81, 0.3);
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.applied-filters-title {
  color: white;
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.filter-list {
  list-style-type: none;
  padding: 0;
  color: #9ca3af; /* gray-400 */
  font-size: 0.875rem;
  text-align: left;
}

.filter-list li {
  margin-bottom: 0.25rem;
  padding-left: 1rem;
  position: relative;
}

.filter-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #4b5563; /* gray-600 */
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(59, 130, 246, 0.2); /* blue-500 with opacity */
  color: #3b82f6; /* blue-500 */
  padding: 0.5rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.action-button:hover {
  background-color: rgba(59, 130, 246, 0.3);
  transform: translateY(-1px);
}

@media (max-width: 640px) {
  .empty-state {
    padding: 1.5rem;
  }
  
  .empty-state-icon {
    font-size: 2rem;
    width: 4rem;
    height: 4rem;
  }
  
  .empty-state-title {
    font-size: 1.25rem;
  }
}
</style>